import { useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Chip,
  FormControlLabel,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { InfoCard, Link, Progress } from '@backstage/core-components';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';
import { STATUS_COLORS, TimelineEvent, useEntityTimeline } from '../health/api';

const SOURCES: Record<string, string> = {
  rollout: 'Выкатка',
  build: 'Сборка',
  merge: 'Мерж',
  config: 'kubeconfig',
  alert: 'Алерт',
  flag: 'Флаг',
  scaling: 'Масштаб',
  restart: 'Рестарты',
  node: 'Нода',
  probe: 'Доступность',
  temporal: 'Temporal',
  'k8s-event': 'Событие k8s',
  logs: 'Логи',
};

const SEVERITY_COLOR: Record<TimelineEvent['severity'], string> = {
  critical: STATUS_COLORS.critical,
  warning: STATUS_COLORS.warning,
  info: '#8A8FA8',
};

const WINDOWS = [
  { hours: 3, label: '3 часа' },
  { hours: 24, label: 'Сутки' },
  { hours: 168, label: '7 дней' },
];

const useStyles = makeStyles(theme => ({
  controls: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },
  filters: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2),
  },
  day: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  event: {
    display: 'grid',
    gridTemplateColumns: '96px 110px 1fr',
    gap: theme.spacing(1.5),
    padding: theme.spacing(0.75, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    alignItems: 'baseline',
  },
  time: { fontVariantNumeric: 'tabular-nums', fontSize: 13 },
  source: {
    fontSize: 12,
    borderLeft: '3px solid',
    paddingLeft: theme.spacing(1),
  },
  summary: { fontSize: 14 },
  meta: { color: theme.palette.text.secondary, fontSize: 12 },
  detail: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  status: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginTop: theme.spacing(2),
  },
}));

const time = (iso: string) =>
  new Date(iso).toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  });

const day = (iso: string) =>
  new Date(iso).toLocaleDateString('ru-RU', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
  });

const EventRow = ({ event, self }: { event: TimelineEvent; self: string }) => {
  const classes = useStyles();
  const color = SEVERITY_COLOR[event.severity];
  let owner: string | undefined;
  if (event.platform) owner = 'платформа';
  else if (event.entity && event.entity !== self)
    owner = event.entity.split('/').pop();
  return (
    <div className={classes.event}>
      <span className={classes.time}>
        {time(event.time)}
        {event.end && event.end !== event.time ? `–${time(event.end)}` : ''}
      </span>
      <span className={classes.source} style={{ borderColor: color }}>
        {SOURCES[event.source] ?? event.source}
      </span>
      <div>
        <div
          className={classes.summary}
          style={event.severity === 'info' ? undefined : { color }}
        >
          {event.url ? (
            <Link to={event.url}>{event.summary}</Link>
          ) : (
            event.summary
          )}
          {owner && <span className={classes.meta}> · {owner}</span>}
        </div>
        {event.details?.filter(Boolean).map((d, i) => (
          <div key={i} className={classes.detail}>
            {d}
          </div>
        ))}
      </div>
    </div>
  );
};

export const TimelineContent = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const ref = stringifyEntityRef(entity);
  const [hours, setHours] = useState(3);
  const [dependencies, setDependencies] = useState(false);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const { value, error, loading } = useEntityTimeline(ref, hours, dependencies);

  const toggle = (source: string) =>
    setHidden(prev => {
      const next = new Set(prev);
      if (next.has(source)) next.delete(source);
      else next.add(source);
      return next;
    });

  let body;
  if (loading && !value) {
    body = <Progress />;
  } else if (!value) {
    body = (
      <Typography color="error">
        Не удалось собрать таймлайн: {error?.message}
      </Typography>
    );
  } else {
    const present = value.sources.filter(s => s.count > 0);
    const events = value.events.filter(e => !hidden.has(e.source));
    const unavailable = value.sources.filter(s => s.status !== 'ok');
    let lastDay = '';
    body = (
      <>
        {present.length > 0 && (
          <Box className={classes.filters}>
            {present.map(s => (
              <Chip
                key={s.source}
                size="small"
                label={`${SOURCES[s.source] ?? s.source}: ${s.count}`}
                variant={hidden.has(s.source) ? 'outlined' : 'default'}
                onClick={() => toggle(s.source)}
              />
            ))}
          </Box>
        )}
        {loading && <Progress />}
        {events.length ? (
          events.map((e, i) => {
            const d = day(e.time);
            const header =
              d !== lastDay ? <div className={classes.day}>{d}</div> : null;
            lastDay = d;
            return (
              <div key={`${e.time}-${i}`}>
                {header}
                <EventRow event={e} self={ref} />
              </div>
            );
          })
        ) : (
          <Typography className={classes.meta}>
            Событий за это время нет
          </Typography>
        )}
        {value.truncated && (
          <Typography className={classes.status}>
            Показаны последние 400 событий
          </Typography>
        )}
        {unavailable.length > 0 && (
          <Typography className={classes.status}>
            Без данных:{' '}
            {unavailable
              .map(
                s =>
                  `${SOURCES[s.source] ?? s.source} (${
                    s.status === 'disabled'
                      ? 'не настроено'
                      : s.error ?? 'ошибка'
                  })`,
              )
              .join('; ')}
          </Typography>
        )}
      </>
    );
  }

  return (
    <InfoCard title="Что происходило">
      <Box className={classes.controls}>
        <ButtonGroup size="small" color="primary">
          {WINDOWS.map(w => (
            <Button
              key={w.hours}
              variant={hours === w.hours ? 'contained' : 'outlined'}
              onClick={() => setHours(w.hours)}
            >
              {w.label}
            </Button>
          ))}
        </ButtonGroup>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={dependencies}
              onChange={e => setDependencies(e.target.checked)}
            />
          }
          label="С зависимостями"
        />
      </Box>
      {body}
    </InfoCard>
  );
};
